import { FC } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Card, Typography } from '@mui/material';
import {
  // DealsAndReservationsDynamicsChart,
  formatAnalyticsPercentage,
  formatBigAnalyticsNumber,
  LotStatusesStatisticsChart,
  LotStatusesStatisticsTable,
  useGetAreaChartsStatisticsQuery,
  useGetLotStatusesStatisticsQuery,
  useGetPriceSquareStatisticsQuery,
  ObjectViewsChart,
  ObjectFavoritesChart,
  ObjectClickContactsChart,
  ObjectCountLikesChart,
  ObjectOpenedSelectionChart,
  ObjectOpenedSelectionLotChart,
  useGetObjectStatisticsQuery,
  getPreviousDateRange,
  AnalyticsPromoCard,
} from 'entities/analytics';
import { TextColor } from 'shared/theme/colors';
import { AnalyticMetricField } from 'shared/ui/Analytics/AnalyticMetricField';
import { AnalyticsBlockCard } from 'shared/ui/Analytics/AnalyticsBlockCard';
import { MegaphonePromoIcon } from 'shared/ui/Analytics/images/megaphone-promo-icon';
import { PackagePromoIcon } from 'shared/ui/Analytics/images/package-promo-icon';
import { LineChartData } from 'shared/ui/Analytics/types';
import { Col } from 'shared/ui/Col';
import { DataInfoIcon } from 'shared/ui/Icons';
import { Row } from 'shared/ui/Row';
import { Spinner } from 'shared/ui/Spinner';
import { IncreaseViewsContent } from '../PromoContents/IncreaseViewsContent';
import { PackagePromotionContent } from '../PromoContents/PackagePromotionContent';

interface ObjectAnalyticsCardProps {
  dateRangeStart: string;
  dateRangeEnd: string;
}

const ChartCardContent = ({
  chartData,
  ChartComponent,
  minWidth = 400,
}: {
  chartData?: LineChartData[];
  ChartComponent: FC<{ pointsData: LineChartData[]; minWidth: number }>;
  minWidth?: number;
}) => {
  const { t } = useTranslation();

  return (
    <Row
      sx={{
        width: '100%',
        height: 223,
        alignItems: 'center',
        justifyContent: 'center',
      }}
      equalsChildrenWidth
    >
      {chartData && chartData.length > 0 ? (
        <ChartComponent pointsData={chartData} minWidth={minWidth} />
      ) : (
        <Row
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            minWidth: 400,
          }}
        >
          <DataInfoIcon
            sx={{
              fontSize: 16,
              color: TextColor.Tertiary,
              position: 'relative',
              top: '1px',
            }}
          />
          <Typography
            variant="accentS"
            color={TextColor.Tertiary}
            sx={{ textAlign: 'center' }}
          >
            {t('analytics.object.noDataText')}
          </Typography>
        </Row>
      )}
    </Row>
  );
};

export const ObjectAnalyticsCard: FC<ObjectAnalyticsCardProps> = ({
  dateRangeStart,
  dateRangeEnd,
}) => {
  const { t } = useTranslation();
  const { objectId } = useParams<{ objectId: string }>();

  const { prevStartDate, prevEndDate } = getPreviousDateRange({
    startDate: dateRangeStart,
    endDate: dateRangeEnd,
  });

  const { data: lotStatusesData, isLoading: isLotStatusesDataLoading } =
    useGetLotStatusesStatisticsQuery({
      objectId: Number(objectId),
    });
  const { data: priceSquareData, isLoading: isPriceSquareDataLoading } =
    useGetPriceSquareStatisticsQuery({
      objectId: Number(objectId),
    });
  const {
    data: areaChartsData,
    isLoading: isAreaChartsDataLoading,
    isFetching: isAreaChartsDataFetching,
  } = useGetAreaChartsStatisticsQuery({
    objectId: Number(objectId),
    startDate: dateRangeStart,
    endDate: dateRangeEnd,
  });
  const {
    data: objectStatisticsData,
    isLoading: isObjectStatisticsDataLoading,
    isFetching: isObjectStatisticsDataFetching,
  } = useGetObjectStatisticsQuery({
    objectId: Number(objectId),
    startDate: dateRangeStart,
    endDate: dateRangeEnd,
    prevStartDate,
    prevEndDate,
  });

  const favoritesChartData = areaChartsData?.favorites;
  const viewsChartData = areaChartsData?.views;
  const clickContactsChartData = areaChartsData?.clickContacts;
  const countLikesChartData = areaChartsData?.countLikes;
  const openedSelectionObjectChartData = areaChartsData?.openedSelectionObject;
  const openedSelectionLotChartData = areaChartsData?.openedSelectionLot;

  const getObjectStatisticsContent = () => {
    return (
      <Row spacing={5}>
        <AnalyticMetricField
          title={t('analytics.object.metricTitle.lastUpdate')}
          value={dayjs(objectStatisticsData?.lastUpdated).format('D MMMM')}
        />
        <AnalyticMetricField
          title={t('analytics.object.metricTitle.viewCount')}
          value={objectStatisticsData?.countViews}
          percentageChange={objectStatisticsData?.countViewsPercent}
        />
        <AnalyticMetricField
          title={t('analytics.object.metricTitle.selections')}
          value={objectStatisticsData?.countSelection}
          percentageChange={objectStatisticsData?.countSelectionPercent}
        />
        <AnalyticMetricField
          title={t('analytics.object.metricTitle.inFavorites')}
          value={objectStatisticsData?.favorites}
          percentageChange={objectStatisticsData?.favoritesPercent}
        />
      </Row>
    );
  };

  const getLotStatusesStatistics = () => {
    if (!lotStatusesData) return null;

    return (
      <Row
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          minWidth: 400,
          display: 'flex',
          gap: 4,
          height: '100%',
        }}
      >
        <LotStatusesStatisticsChart data={lotStatusesData} />
        <LotStatusesStatisticsTable data={lotStatusesData} />
      </Row>
    );
  };

  const getLotPricesAndAreasStatisticsContent = () => {
    return (
      <Row spacing={5}>
        <AnalyticMetricField
          title={t('analytics.object.metricTitle.soldPercentage')}
          value={formatAnalyticsPercentage(priceSquareData?.salePercent)}
        />
        <AnalyticMetricField
          title={t('analytics.object.metricTitle.priceInSale')}
          value={formatBigAnalyticsNumber(priceSquareData?.averagePriceSale)}
        />
        <AnalyticMetricField
          title={t('analytics.object.metricTitle.soldPrice')}
          value={formatBigAnalyticsNumber(priceSquareData?.averagePriceSold)}
        />
        <AnalyticMetricField
          title={t('analytics.object.metricTitle.areaInSale')}
          value={priceSquareData?.averageSquareSale}
        />
        <AnalyticMetricField
          title={t('analytics.object.metricTitle.soldArea')}
          value={priceSquareData?.averageSquareSold}
        />
      </Row>
    );
  };

  // const getDealsAndReservationsDynamics = () => {
  //   return (
  //     <Row sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
  //       <DealsAndReservationsDynamicsChart />
  //     </Row>
  //   );
  // };

  const isSomeDataLoading =
    isLotStatusesDataLoading ||
    isPriceSquareDataLoading ||
    isAreaChartsDataLoading ||
    isObjectStatisticsDataLoading;

  return (
    <Card sx={{ py: 9, px: 8, mb: 8 }}>
      <Typography variant="headerM" sx={{ mb: 9 }}>
        {t('analytics.object.title')}
      </Typography>

      {isSomeDataLoading ? (
        <Col
          sx={{
            width: '100%',
            height: '400px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Spinner />
          <Typography variant="accentS">
            {t('analytics.object.loadingText')}
          </Typography>
        </Col>
      ) : (
        <Col spacing={9}>
          <Col spacing={8}>
            <Row spacing={8} equalsChildrenWidth>
              <AnalyticsBlockCard
                title={t('analytics.object.blockTitle.objectStatistics')}
                afterTitleSpacing={9}
                height={'100%'}
                isLoading={isObjectStatisticsDataFetching}
              >
                {getObjectStatisticsContent()}
              </AnalyticsBlockCard>
              <AnalyticsBlockCard
                title={t('analytics.object.blockTitle.lotStatuses')}
                height={'100%'}
              >
                {getLotStatusesStatistics()}
              </AnalyticsBlockCard>
            </Row>

            <Row
              spacing={8}
              equalsChildrenWidth
              sx={{ justifyContent: 'space-between' }}
            >
              <AnalyticsPromoCard
                title={t('analytics.object.promo.increaseViews.title')}
                subtitle={t('analytics.object.promo.increaseViews.subtitle')}
                minWidth={560}
                modalContent={<IncreaseViewsContent />}
                image={
                  <MegaphonePromoIcon
                    sx={{
                      width: { xs: '160px', xl: '180px' },
                      height: { xs: '160px', xl: '180px' },
                    }}
                  />
                }
              />

              <AnalyticsPromoCard
                title={t('analytics.object.promo.packagePromotion.title')}
                subtitle={t('analytics.object.promo.packagePromotion.subtitle')}
                minWidth={560}
                modalContent={<PackagePromotionContent />}
                modalMaxWidth={700}
                image={
                  <PackagePromoIcon
                    sx={{
                      width: { xs: '200px', xl: '220px' },
                      height: { xs: '200px', xl: '220px' },
                    }}
                  />
                }
              />
            </Row>

            <Row spacing={8} equalsChildrenWidth>
              <AnalyticsBlockCard
                title={t('analytics.object.blockTitle.lotPricesAndAreas')}
              >
                {getLotPricesAndAreasStatisticsContent()}
              </AnalyticsBlockCard>
              {/* <AnalyticsBlockCard
              title={t('analytics.object.blockTitle.dealsAndReservations')}
              afterTitleSpacing={8}
              height={540}
            >
              {getDealsAndReservationsDynamics()}
            </AnalyticsBlockCard> */}
            </Row>

            <Row spacing={8} equalsChildrenWidth>
              <AnalyticsBlockCard
                title={t('analytics.object.blockTitle.objectViews')}
                isLoading={isAreaChartsDataFetching}
              >
                <ChartCardContent
                  chartData={viewsChartData}
                  ChartComponent={ObjectViewsChart}
                />
              </AnalyticsBlockCard>
              <AnalyticsBlockCard
                title={t('analytics.object.blockTitle.objectFavorites')}
                isLoading={isAreaChartsDataFetching}
              >
                <ChartCardContent
                  chartData={favoritesChartData}
                  ChartComponent={ObjectFavoritesChart}
                />
              </AnalyticsBlockCard>
            </Row>

            <Row spacing={8} equalsChildrenWidth>
              <AnalyticsBlockCard
                title={t('analytics.object.blockTitle.objectOpenedSelection')}
                isLoading={isAreaChartsDataFetching}
              >
                <ChartCardContent
                  chartData={openedSelectionObjectChartData}
                  ChartComponent={ObjectOpenedSelectionChart}
                />
              </AnalyticsBlockCard>
              <AnalyticsBlockCard
                title={t(
                  'analytics.object.blockTitle.objectOpenedSelectionLot'
                )}
                isLoading={isAreaChartsDataFetching}
              >
                <ChartCardContent
                  chartData={openedSelectionLotChartData}
                  ChartComponent={ObjectOpenedSelectionLotChart}
                />
              </AnalyticsBlockCard>
            </Row>

            <Row spacing={8} equalsChildrenWidth>
              <AnalyticsBlockCard
                title={t('analytics.object.blockTitle.objectCountLikes')}
                isLoading={isAreaChartsDataFetching}
              >
                <ChartCardContent
                  chartData={countLikesChartData}
                  ChartComponent={ObjectCountLikesChart}
                />
              </AnalyticsBlockCard>
              <AnalyticsBlockCard
                title={t('analytics.object.blockTitle.objectClickContacts')}
                isLoading={isAreaChartsDataFetching}
              >
                <ChartCardContent
                  chartData={clickContactsChartData}
                  ChartComponent={ObjectClickContactsChart}
                />
              </AnalyticsBlockCard>
            </Row>
          </Col>
        </Col>
      )}
    </Card>
  );
};
